import { useEffect, useMemo, useRef, useCallback } from "react";
import { useUpdateEffect } from "react-use";
import Chat from "../../components/chat/chat-components";
import { useAppStore } from "../../stores/appStore";
import { OnboardingScreen } from "../../components/onboarding/onboarding-screen";
import useUserStore from "../../stores/userStore";
import correctAnswer from "../../assets/sound/correct.mp4";
import wrongAnswer from "../../assets/sound/wrong.mp4";
import { playAchiveSound } from "../../helpers/achiveSound";
import { playAppSound } from "../../helpers/playAppSound";
// import { LiveSpeechScreen } from "../../components/live-speech-screen/live-speech-screen";
import { PortalContext } from "../../PortalContext";
import { HeyGenAvatarDialog } from "../../components/heygen-avatar/heygen-avatar-dialog/heygen-avatar-dialog";
import { postRealtimeMenu } from "../../axios/realtimeSpeech";
import { LiveSpeechStageDialog } from "../../components/live-speech-stage/live-speech-stage-dialog";
import { AsyncRoleplayDialog } from "../../components/async-roleplay/async-roleplay-dialog/async-roleplay-dialog";

const ChatScreen = () => {
  const { user, token } = useUserStore();
  const {
    focus,
    domain,
    playSoundAnswerIsCorrect,
    setPlaySoundAnswerIsCorrect,
    playSoundBadge,
    setPlaySoundBadge,
    sellingDialogue,
    liveSpeechMode,
    realtimePersona,
    realtimeMaturityLevel,
    realtimeMenuOptions,
    lessonDialogue,
    lessonDialogueMetadata,
    setSellingDialogue,
    setLiveSpeechMode,
    setRealtimePersona,
    setRealtimeMaturityLevel,
    setRealtimeMenuOptions,
    setLessonDialogue,
    setLessonDialogueMetadata,
    setIsBrowserLocked,
    setLessonTurnsAllowed,
    setLessonTurnsUsed,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    asyncRoleplayOpen,
    asyncRoleplaySessionId,
    setAsyncRoleplayOpen,
    setAsyncRoleplaySessionId,
    setAsyncRoleplayRecordId,
    setRealtimeDetailsWindowText,
  } = useAppStore();

  const sellingAuth = useMemo(() => {
    const audioOnly = Boolean((realtimeMenuOptions as any)?.audio_only);
    return {
      token,
      focus,
      domain,
      // include all selected menu params in auth on socket connect
      ...realtimeMenuOptions,
      realtime_mode: liveSpeechMode,
      persona: realtimePersona?.text,
      maturity_level: realtimeMaturityLevel,
      selling_dialogue: true,
      audio_only: audioOnly,
    };
  }, [
    token,
    focus,
    domain,
    realtimeMenuOptions,
    liveSpeechMode,
    realtimePersona?.text,
    realtimeMaturityLevel,
  ]);

  useUpdateEffect(() => {
    if (!lessonDialogue) {
      setIsBrowserLocked(false);
    }
  }, [lessonDialogue]);

  useUpdateEffect(() => {
    if (sellingDialogue && liveSpeechMode && realtimePersona) {
      setLessonTurnsUsed(0);
    }
  }, [sellingDialogue, liveSpeechMode, realtimePersona, setLessonTurnsUsed]);

  const handleSellingConnectionClose = useCallback(async () => {
    const wasCoachMode = liveSpeechMode === "coach_me";
    const recordId = realtimeMenuOptions?.record_id;

    setSellingDialogue(false);
    setLiveSpeechMode(null);
    setRealtimePersona(null);
    setRealtimeMaturityLevel(null);
    setRealtimeMenuOptions({});
    setLessonTurnsUsed(0);
    setRealtimeDetailsWindowText(null);

    // If coaching mode was completed, call the endpoint to complete coaching
    if (wasCoachMode && recordId) {
      try {
        await postRealtimeMenu({
          record_id: recordId,
          realtime_mode: "coach_me",
          complete: true,
        });
      } catch (error) {
        console.error("Error completing coaching mode:", error);
      }
    }
  }, [
    liveSpeechMode,
    realtimeMenuOptions,
    setSellingDialogue,
    setLiveSpeechMode,
    setRealtimePersona,
    setRealtimeMaturityLevel,
    setRealtimeMenuOptions,
    setLessonTurnsUsed,
    setRealtimeDetailsWindowText,
  ]);

  // Same sequence as menu "Go" for Audio: tear down current roleplay shell,
  // post audio_only menu, then open LiveSpeechStageDialog fresh.
  const handleContinueAudioOnly = useCallback(async () => {
    const savedMode = liveSpeechMode;
    const savedPersona = realtimePersona;
    const nextOptions = {
      ...realtimeMenuOptions,
      audio_only: true,
    };

    setLessonSecondsAllowed(null);
    setLessonSecondsRemaining(null);

    // Video endSession already ran in HeyGen; unmount the shell and give the
    setSellingDialogue(false);

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 1500);
    });

    try {
      const response = await postRealtimeMenu(nextOptions);
      if (response?.error) {
        console.error("Failed to start audio-only roleplay:", response.error);
        if (savedMode) setLiveSpeechMode(savedMode);
        if (savedPersona) setRealtimePersona(savedPersona);
        setRealtimeMenuOptions(realtimeMenuOptions);
        setSellingDialogue(true);
        return;
      }
    } catch (error) {
      console.error("Failed to start audio-only roleplay:", error);
      if (savedMode) setLiveSpeechMode(savedMode);
      if (savedPersona) setRealtimePersona(savedPersona);
      setRealtimeMenuOptions(realtimeMenuOptions);
      setSellingDialogue(true);
      return;
    }

    // Let /realtime/menu apply on staging before the audio socket connects.
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 300);
    });

    if (savedMode) setLiveSpeechMode(savedMode);
    if (savedPersona) setRealtimePersona(savedPersona);
    setRealtimeMenuOptions(nextOptions);
    setSellingDialogue(true);
  }, [
    liveSpeechMode,
    realtimePersona,
    realtimeMenuOptions,
    setLessonSecondsAllowed,
    setLessonSecondsRemaining,
    setLiveSpeechMode,
    setRealtimePersona,
    setRealtimeMenuOptions,
    setSellingDialogue,
  ]);

  const handleLessonConnectionClose = useCallback(() => {
    setLessonDialogue(false);
    setLessonDialogueMetadata(null);
    setLessonTurnsAllowed(null);
    setLessonTurnsUsed(0);
  }, [
    setLessonDialogue,
    setLessonDialogueMetadata,
    setLessonTurnsAllowed,
    setLessonTurnsUsed,
  ]);

  const handleAsyncRoleplayClose = useCallback(() => {
    // Closing pauses the conversation; it does not finish the session.
    // session_id/record_id remain in localStorage for resume.
    setAsyncRoleplayOpen(false);
    setAsyncRoleplaySessionId(null);
    setAsyncRoleplayRecordId(null);
    setLiveSpeechMode(null);
    setRealtimePersona(null);
    setRealtimeMenuOptions({});
  }, [
    setAsyncRoleplayOpen,
    setAsyncRoleplaySessionId,
    setAsyncRoleplayRecordId,
    setLiveSpeechMode,
    setRealtimePersona,
    setRealtimeMenuOptions,
  ]);

  // Handle event for forced closing of sessions when switching to another tab/device
  useEffect(() => {
    const handleForceCloseSessions = async () => {
      if (sellingDialogue) {
        await handleSellingConnectionClose();
      }
      if (lessonDialogue) {
        handleLessonConnectionClose();
      }
      if (asyncRoleplayOpen) {
        handleAsyncRoleplayClose();
      }
    };

    window.addEventListener(
      "forceCloseActiveSessions",
      handleForceCloseSessions
    );

    return () => {
      window.removeEventListener(
        "forceCloseActiveSessions",
        handleForceCloseSessions
      );
    };
  }, [
    sellingDialogue,
    lessonDialogue,
    asyncRoleplayOpen,
    handleSellingConnectionClose,
    handleLessonConnectionClose,
    handleAsyncRoleplayClose,
  ]);

  // const sellingAuth = useMemo(
  //   () => ({
  //     token,
  //     focus,
  //     domain,
  //     realtime_mode: liveSpeechMode,
  //     persona: realtimePersona?.text,
  //     maturity_level: realtimeMaturityLevel,
  //     selling_dialogue: true,
  //   }),
  //   [
  //     token,
  //     focus,
  //     domain,
  //     liveSpeechMode,
  //     realtimePersona?.text,
  //     realtimeMaturityLevel,
  //   ]
  // );

  // const lessonAuth = useMemo(
  //   () => ({
  //     token,
  //     focus,
  //     domain,
  //     lesson_dialogue: true,
  //     metadata: lessonDialogueMetadata,
  //     persona: realtimePersona?.text ?? undefined,
  //     maturity_level: realtimeMaturityLevel ?? undefined,
  //   }),
  //   [
  //     token,
  //     focus,
  //     domain,
  //     lessonDialogueMetadata,
  //     realtimePersona?.text,
  //     realtimeMaturityLevel,
  //   ]
  // );

  const portalContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (playSoundAnswerIsCorrect === true) {
      playAppSound(correctAnswer).catch((err) =>
        console.error("Audio play error (correct):", err),
      );
      setPlaySoundAnswerIsCorrect(null);
    } else if (playSoundAnswerIsCorrect === false) {
      playAppSound(wrongAnswer).catch((err) =>
        console.error("Audio play error (wrong):", err),
      );
      setPlaySoundAnswerIsCorrect(null);
    }
  }, [playSoundAnswerIsCorrect, setPlaySoundAnswerIsCorrect]);

  useEffect(() => {
    if (playSoundBadge === true) {
      playAchiveSound();
      setPlaySoundBadge(false);
    }
  }, [playSoundBadge, setPlaySoundBadge]);

  // const stageTempAuth = useMemo(() => {
  //   return {
  //     token,
  //     focus,
  //     domain,
  //     ...realtimeMenuOptions,
  //     realtime_mode:
  //       liveSpeechMode ??
  //       (realtimeMenuOptions as any)?.realtime_mode ??
  //       "selling_dialogue",
  //     persona: realtimePersona?.text,
  //     maturity_level: realtimeMaturityLevel,
  //     selling_dialogue: true,
  //     audio_only: true,
  //   };
  // }, [
  //   token,
  //   focus,
  //   domain,
  //   realtimeMenuOptions,
  //   liveSpeechMode,
  //   realtimePersona?.text,
  //   realtimeMaturityLevel,
  // ]);

  // const handleOpenStageTemp = async () => {
  //   if (!token) return;

  //   try {
  //     // Ensure we have a persona/avatar like LiveSpeechScreen expects.
  //     if (!realtimePersona) {
  //       const personaMenu = await getPersonaMenu();
  //       const personaOption =
  //         (personaMenu as any)?.options?.[0] ||
  //         (Array.isArray(personaMenu) && (personaMenu as any)[0]?.options?.[0]) ||
  //         (Array.isArray(personaMenu) ? (personaMenu as any)[0] : null);

  //       if (personaOption && typeof personaOption === "object") {
  //         setRealtimePersona(personaOption as any);
  //       }
  //     }

  //     if (!realtimeMaturityLevel) {
  //       const levelsMenu = await getLevelsMenu();
  //       const levelOption =
  //         (levelsMenu as any)?.options?.[0] ||
  //         (Array.isArray(levelsMenu) && (levelsMenu as any)[0]?.options?.[0]) ||
  //         (Array.isArray(levelsMenu) ? (levelsMenu as any)[0] : null);

  //       const levelText =
  //         typeof levelOption === "string"
  //           ? levelOption
  //           : typeof (levelOption as any)?.text === "string"
  //             ? (levelOption as any).text
  //             : null;
  //       if (levelText) setRealtimeMaturityLevel(levelText);
  //     }

  //     if (!liveSpeechMode) {
  //       setLiveSpeechMode("selling_dialogue");
  //     }
  //   } catch (e) {
  //     console.warn("Failed to preload persona/level for Stage temp:", e);
  //   }

  //   setStageOpen(true);
  // };

  if (user.id && user.need_onboarding) {
    return (
      <PortalContext.Provider value={portalContainerRef}>
        {" "}
        <OnboardingScreen />{" "}
      </PortalContext.Provider>
    );
  }

  return (
    <PortalContext.Provider value={portalContainerRef}>
      <div
        ref={portalContainerRef}
        className="full-screen flex flex-row w-full relative"
      >
        <Chat />

        {/* TEMP: open LiveSpeechStageDialog directly (bypasses realtime menu) */}
        {/* {!sellingDialogue && !lessonDialogue && token && !stageOpen && (
          <Button
            variant="contained"
            onClick={handleOpenStageTemp}
            sx={{
              position: "absolute",
              right: 16,
              bottom: 16,
              zIndex: 2000,
              backgroundColor: "rgba(0,0,0,0.65)",
              border: "1px solid rgba(255,255,255,0.15)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
            }}
          >
            Open Stage (temp)
          </Button>
        )}

        <LiveSpeechStageDialog
          open={stageOpen}
          onClose={() => setStageOpen(false)}
          auth={stageTempAuth}
          additionalMessageProps={{ selling_dialogue: true, audio_only: true }}
          title="Stage (temp)"
        /> */}

        {/* 
        if (
          sellingDialogue &&
          liveSpeechMode &&
          realtimePersona &&
          realtimeMaturityLevel
        ) {
          return (
            <LiveSpeechScreen
              auth={sellingAuth}
              additionalMessageProps={{ selling_dialogue: true }}
              onConnectionClose={handleSellingConnectionClose}
            />
          );
        }

        if (lessonDialogue) {
          return (
            <LiveSpeechScreen
              auth={lessonAuth}
              additionalMessageProps={{ lesson_dialogue: true }}
              onConnectionClose={handleLessonConnectionClose}
            />
          );
        }
        */}

        {/* Selling dialogue: choose UI by realtimeMenuOptions.audio_only */}
        {sellingDialogue &&
          Boolean((realtimeMenuOptions as any)?.audio_only) &&
          liveSpeechMode && (
            <LiveSpeechStageDialog
              key="selling-audio-only"
              open={sellingDialogue}
              onClose={handleSellingConnectionClose}
              auth={sellingAuth}
              additionalMessageProps={{
                selling_dialogue: true,
                audio_only: true,
              }}
              title="Audio only"
            />
          )}

        {sellingDialogue &&
          !Boolean((realtimeMenuOptions as any)?.audio_only) &&
          liveSpeechMode && (
            <HeyGenAvatarDialog
              open={sellingDialogue}
              onClose={handleSellingConnectionClose}
              onFallbackAudioOnly={handleContinueAudioOnly}
              avatarId={(realtimePersona as any)?.avatar_id}
              voiceId={(realtimePersona as any)?.voice_id}
              auth={{
                token,
                focus,
                domain,
                ...Object.fromEntries(
                  Object.entries(realtimeMenuOptions).filter(
                    ([key]) =>
                      !key.toLowerCase().includes("maturity") &&
                      !key.toLowerCase().includes("level")
                  )
                ),
                realtime_mode: liveSpeechMode,
                persona: realtimePersona?.text || "Doctor",
                selling_dialogue: true,
                audio_only: false,
              }}
            />
          )}

        {/* HeyGen Avatar Dialog for Lesson Dialogue */}
        {lessonDialogue && (
          <HeyGenAvatarDialog
            open={lessonDialogue}
            onClose={handleLessonConnectionClose}
            avatarId={
              (lessonDialogueMetadata as any)?.avatar_id ||
              (realtimePersona as any)?.avatar_id
            }
            voiceId={
              (lessonDialogueMetadata as any)?.voice_id ||
              (realtimePersona as any)?.voice_id
            }
            auth={{
              token,
              focus,
              domain,
              lesson_dialogue: true,
              metadata: lessonDialogueMetadata,
              persona: realtimePersona?.text ?? undefined,
              maturity_level: realtimeMaturityLevel ?? undefined,
            }}
          />
        )}

        {asyncRoleplayOpen && asyncRoleplaySessionId && (
          <AsyncRoleplayDialog
            open={asyncRoleplayOpen}
            sessionId={asyncRoleplaySessionId}
            onClose={handleAsyncRoleplayClose}
          />
        )}
      </div>
    </PortalContext.Provider>
  );
};

export default ChatScreen;
